import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { toast } from 'sonner';
import { Building2, Users, Eye, EyeOff, ArrowLeft, ArrowRight } from 'lucide-react';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  
  const [step, setStep] = useState(1); // 1: Choose type, 2: Fill details
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [form, setForm] = useState({
    userType: '', // 'builder' or 'channel_partner'
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    rera_number: '',
  });

  const handleTypeSelect = (type) => {
    setForm({ ...form, userType: type });
  };

  const handleNext = () => {
    if (!form.userType) {
      toast.error('Please select your account type');
      return;
    }
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const generateEmail = () => {
    // Generate suggested email based on user type
    const prefix = form.userType === 'builder' ? 'builder' : 'cp';
    return `${prefix}@effy.com`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.password !== form.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!form.email && !form.phone) {
      toast.error('Please provide email or phone number');
      return;
    }

    setLoading(true);
    try {
      const role = form.userType === 'builder' ? 'builder_admin' : 'partner';
      
      const userData = {
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        password: form.password,
        role: role,
        company_name: form.company_name || null,
        rera_number: form.rera_number || null,
      };

      await register(userData);
      
      const welcomeMessage = form.userType === 'builder' 
        ? 'Welcome to effyRealty! Your builder account is ready.'
        : 'Welcome to effyRealty! Your Channel Partner account is pending approval.';
      
      toast.success(welcomeMessage);
      navigate(form.userType === 'builder' ? '/builder' : '/partner');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left side - Hero */}
      <div 
        className="hidden md:flex md:w-1/2 bg-cover bg-center relative"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1758599543152-a73184816eba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGhhbmRzaGFrZSUyMG1lZXRpbmclMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzcwMTk1NTcxfDA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="flex items-center gap-3 mb-8">
            <Building2 className="h-10 w-10 text-amber-500" />
            <span className="text-3xl font-bold tracking-tight">effyRealty</span>
          </div>
          <h1 className="text-4xl font-bold mb-4">
            {step === 1 ? 'Join effyRealty' : (form.userType === 'builder' ? 'Builder Registration' : 'Channel Partner Registration')}
          </h1>
          <p className="text-lg text-slate-300 max-w-md">
            {step === 1 
              ? 'Choose how you want to use effyRealty - as a Builder or Channel Partner.'
              : (form.userType === 'builder' 
                ? 'Manage your projects, partners, and sales team all in one place.'
                : 'Partner with top builders and grow your real estate business.'
              )
            }
          </p>
          {step === 2 && form.userType === 'channel_partner' && (
            <ul className="mt-8 space-y-3 text-slate-300">
              <li className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-amber-500" />
                Access premium projects
              </li>
              <li className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-amber-500" />
                Build your own microsites
              </li>
              <li className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-amber-500" />
                Track commissions transparently
              </li>
            </ul>
          )}
          {step === 2 && form.userType === 'builder' && (
            <ul className="mt-8 space-y-3 text-slate-300">
              <li className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-amber-500" />
                Manage multiple projects
              </li>
              <li className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-amber-500" />
                Onboard channel partners
              </li>
              <li className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-amber-500" />
                Track sales team performance
              </li>
            </ul>
          )}
        </div>
      </div>

      {/* Right side - Register form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 md:hidden">
            <Building2 className="h-8 w-8 text-amber-600" />
            <span className="text-2xl font-bold">effyRealty</span>
          </div>

          {step === 1 ? (
            /* Step 1: Choose Account Type */
            <Card className="border-border">
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl font-bold" data-testid="register-title">Create an account</CardTitle>
                <CardDescription>
                  Choose your account type to get started
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <RadioGroup 
                  value={form.userType} 
                  onValueChange={handleTypeSelect}
                  className="space-y-4"
                >
                  {/* Builder Option */}
                  <div 
                    className={`relative flex items-start p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      form.userType === 'builder' 
                        ? 'border-amber-500 bg-amber-50' 
                        : 'border-border hover:border-amber-200 hover:bg-slate-50'
                    }`}
                    onClick={() => handleTypeSelect('builder')}
                  >
                    <RadioGroupItem value="builder" id="builder" className="mt-1" />
                    <div className="ml-3 flex-1">
                      <Label htmlFor="builder" className="text-base font-semibold cursor-pointer flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-amber-600" />
                        Builder / Developer
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        I'm a real estate developer looking to manage projects and partner network
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Email format: builder@yourcompany.com
                      </p>
                    </div>
                  </div>

                  {/* Channel Partner Option */}
                  <div 
                    className={`relative flex items-start p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      form.userType === 'channel_partner' 
                        ? 'border-amber-500 bg-amber-50' 
                        : 'border-border hover:border-amber-200 hover:bg-slate-50'
                    }`}
                    onClick={() => handleTypeSelect('channel_partner')}
                  >
                    <RadioGroupItem value="channel_partner" id="channel_partner" className="mt-1" />
                    <div className="ml-3 flex-1">
                      <Label htmlFor="channel_partner" className="text-base font-semibold cursor-pointer flex items-center gap-2">
                        <Users className="h-5 w-5 text-indigo-600" />
                        Channel Partner
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        I'm a broker/agent looking to sell properties and earn commissions
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Email format: cp@yourcompany.com
                      </p>
                    </div>
                  </div>
                </RadioGroup>

                <Button 
                  onClick={handleNext}
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800"
                  disabled={!form.userType}
                  data-testid="btn-next"
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>

                <div className="text-center text-sm">
                  <span className="text-muted-foreground">Already have an account? </span>
                  <Link to="/login" className="text-amber-600 hover:text-amber-700 font-medium" data-testid="link-login">
                    Sign in
                  </Link>
                </div>
              </CardContent>
            </Card>
          ) : (
            /* Step 2: Registration Form */
            <Card className="border-border">
              <CardHeader className="space-y-1">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={handleBack} className="p-0 h-8 w-8">
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div>
                    <CardTitle className="text-2xl font-bold">
                      {form.userType === 'builder' ? 'Builder Registration' : 'Channel Partner Registration'}
                    </CardTitle>
                    <CardDescription>
                      {form.userType === 'builder' 
                        ? 'Set up your builder account'
                        : 'Register as a Channel Partner (CP)'
                      }
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      placeholder="Your full name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      data-testid="input-name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">
                      {form.userType === 'builder' ? 'Company/Builder Name *' : 'Company Name (Optional)'}
                    </Label>
                    <Input
                      id="company"
                      placeholder={form.userType === 'builder' ? 'Your development company name' : 'Your company or agency name'}
                      value={form.company_name}
                      onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                      required={form.userType === 'builder'}
                      data-testid="input-company"
                    />
                  </div>

                  {form.userType === 'channel_partner' && (
                    <div className="space-y-2">
                      <Label htmlFor="rera">RERA Number (Optional)</Label>
                      <Input
                        id="rera"
                        placeholder="Your RERA registration number"
                        value={form.rera_number}
                        onChange={(e) => setForm({ ...form, rera_number: e.target.value })}
                        data-testid="input-rera"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={form.userType === 'builder' ? 'builder@yourcompany.com' : 'cp@yourcompany.com'}
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                      data-testid="input-reg-email"
                    />
                    <p className="text-xs text-muted-foreground">
                      {form.userType === 'builder' 
                        ? 'Your sales team will get emails like: sales@yourcompany.com'
                        : 'Your sub-users will get emails like: agent1@yourcompany.com'
                      }
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+91 9876543210"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid="input-reg-phone"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Create a password (min 6 characters)"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        required
                        data-testid="input-reg-password"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password *</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm your password"
                      value={form.confirmPassword}
                      onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                      required
                      data-testid="input-confirm-password"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-slate-900 hover:bg-slate-800" 
                    disabled={loading}
                    data-testid="btn-register"
                  >
                    {loading ? 'Creating account...' : (form.userType === 'builder' ? 'Create Builder Account' : 'Register as Channel Partner')}
                  </Button>
                </form>

                <div className="mt-6 text-center text-sm">
                  <span className="text-muted-foreground">Already have an account? </span>
                  <Link to="/login" className="text-amber-600 hover:text-amber-700 font-medium" data-testid="link-login">
                    Sign in
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
